import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, Target, Type, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { TryNowDemo } from '../components/landing/TryNowDemo';
import { LiveTestimonials } from '../components/landing/LiveTestimonials';
import { HeroLayerShowcase } from '../components/landing/HeroLayerShowcase';
import { BreathingGlowLoader } from '../components/landing/BreathingGlowLoader';
import { InteractiveBackground } from '../components/landing/InteractiveBackground';
import { MetaPromptSection } from '../components/landing/MetaPromptSection';
import { ComparisonSection } from '../components/landing/ComparisonSection';
import { SEO } from '../components/SEO';

const VP = { once: true, margin: "-80px" };
const EASE = [0.16, 1, 0.3, 1];

const FEATURES = [
  {
    tag: 'Layer 1 · Built', tagColor: '#A855F7',
    title: 'PI Check',
    desc: 'Real-time receiver emotional outcome prediction. Scores your message 0–100 for conflict probability and tells you exactly how the receiver will feel — before you hit send.',
    pills: ['12 outcome modes', '8 relationship contexts', '17 profession profiles', 'PI Score 0–100'],
    span: 'md:col-span-2',
  },
  {
    tag: 'Layer 2 · Built · Expanding', tagColor: '#22D3EE',
    title: 'PI Craft',
    desc: "Auto-detects which AI you're in and structures your cast using that platform's published best practices.",
    pills: ['ChatGPT', 'Claude', 'Gemini', 'Perplexity', 'Copilot', 'Cursor', 'Emergent'],
    span: '',
  },
  {
    tag: 'Layer 3 · New', tagColor: '#34D399',
    title: 'Token Optimizer',
    desc: 'Same output quality. 40–70% fewer tokens. Live cost shown before you send.',
    pills: ['Live cost meter', 'Cache-aware structuring', 'Model routing'],
    span: '',
  },
  {
    tag: 'Layer 4 · New', tagColor: '#FBBF24',
    title: 'PI Cast',
    desc: 'State any outcome in plain English. PI Cast generates a precision cast for any AI platform, any use case, any profession.',
    pills: ['20 outcome categories', 'Image gen casts', 'Code & debugging'],
    span: 'md:col-span-2',
  },
  {
    tag: 'Layer 5 · New', tagColor: '#A855F7',
    title: 'PI Loop',
    desc: 'Automated outcome refinement. PI Loop generates, evaluates, and refines silently until the result meets your threshold.',
    pills: ['Auto-evaluate', 'Silent refinement', 'Outcome threshold gate'],
    span: '',
  },
  {
    tag: 'Layer 6 · Building', tagColor: '#22D3EE',
    title: 'PI Store',
    desc: 'Create, sell, and discover quality-scored casts. Every cast shows token count, cost-per-call, quality score, and platform compatibility.',
    pills: ['Stripe payouts', 'Quality scoring', '80% revenue share'],
    span: '',
  },
];

const HOW_IT_WORKS = [
  { num: 1, title: 'You Type', desc: 'Start typing in any web app — Gmail, Slack, ChatGPT, Claude, anywhere. No setup needed.', icon: Type },
  { num: 2, title: 'SendPI Detects', desc: 'Auto-detects human-to-human or human-to-AI context. Selects the right layer automatically.', icon: Eye },
  { num: 3, title: 'Score / Cast', desc: 'PI Check scores. PI Craft structures. Token Optimizer trims. All in real time.', icon: BarChart3 },
  { num: 4, title: 'Outcome Delivered', desc: 'Rewrite applied. Precision cast sent. PI Loop refines if needed. Exactly what you intended.', icon: Target },
];

export default function LandingPage() {
  const [loaderDone, setLoaderDone] = useState(() => !!sessionStorage.getItem('sendpi_loader_seen'));
  const handleLoaderComplete = useCallback(() => setLoaderDone(true), []);

  return (
    <div className="min-h-screen bg-[#030303]" data-testid="landing-page">
      <SEO
        title={null}
        description="SendPI is the communication layer for both human and AI. Predict how your messages land before you send them. Score 0-100 for conflict probability, rewrite to achieve desired outcomes, and generate precision AI casts from plain English. Free 200 credits."
        path="/"
      />
      {!loaderDone && <BreathingGlowLoader onComplete={handleLoaderComplete} />}

      {/* Interactive constellation + cursor glow background */}
      <InteractiveBackground />

      <Navbar />

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        {/* Volumetric background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-[0.08]"
            style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
          <div className="absolute bottom-[10%] right-[10%] w-[600px] h-[600px] rounded-full opacity-[0.05]"
            style={{ background: 'radial-gradient(circle, #00E5FF 0%, transparent 70%)' }} />
          <div className="absolute top-[60%] left-[5%] w-[400px] h-[400px] rounded-full opacity-[0.04]"
            style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)' }} />
          {/* Subtle grid overlay */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        </div>

        <div className="relative z-[2] max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div className="space-y-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: loaderDone ? 0 : 3, duration: 0.8 }}>
              <div className="space-y-7">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: loaderDone ? 0.1 : 3.1, duration: 0.6 }}>
                  <span className="inline-flex items-center gap-2.5 text-[10px] uppercase tracking-[0.25em] text-white/50 font-medium">
                    <span className="w-6 h-px bg-gradient-to-r from-[#A855F7] to-transparent" />
                    Communication Intelligence · Patents Pending
                  </span>
                </motion.div>

                <h1 className="font-outfit text-5xl sm:text-6xl lg:text-[5.5rem] font-bold tracking-tighter leading-[0.95]">
                  <motion.span
                    className="block"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: loaderDone ? 0.2 : 3.2, duration: 0.8, ease: EASE }}
                  >
                    <span className="relative inline-block">
                      <span className="relative z-10 bg-clip-text text-transparent"
                        style={{
                          backgroundImage: 'linear-gradient(135deg, #C084FC 0%, #818CF8 25%, #22D3EE 50%, #A78BFA 75%, #E879F9 100%)',
                          backgroundSize: '200% 200%',
                          animation: 'gradientShift 4s ease infinite',
                          filter: 'drop-shadow(0 0 40px rgba(168,85,247,0.5)) drop-shadow(0 0 80px rgba(34,211,238,0.3))',
                        }}
                      >
                        SendPI
                      </span>
                    </span>
                  </motion.span>
                  <motion.span
                    className="block text-white mt-2"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: loaderDone ? 0.35 : 3.35, duration: 0.8, ease: EASE }}
                  >
                    State the outcome.
                  </motion.span>
                  <motion.span
                    className="block text-white/50 text-[0.5em] mt-1 font-light"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: loaderDone ? 0.5 : 3.5, duration: 0.7, ease: EASE }}
                  >
                    Get it.
                  </motion.span>
                </h1>

                <motion.p
                  className="text-base text-white/55 max-w-lg leading-relaxed font-light"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: loaderDone ? 0.6 : 3.6, duration: 0.8 }}
                >
                  <span className="block text-[#C084FC] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Predictive Communication Intelligence Layer</span>
                  Predicting how your receiver will feel before you hit send, and turning one line of plain English into expert AI results.
                </motion.p>
              </div>

              <motion.div className="flex flex-wrap gap-4" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: loaderDone ? 0.7 : 3.7, duration: 0.6 }}>
                <a href="#try-now">
                  <Button className="shimmer-btn text-white rounded-full px-8 py-6 text-base font-outfit font-semibold border-0" data-testid="hero-try-btn">
                    Try It Free <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a href="#features">
                  <Button variant="outline" className="border-white/15 text-white/60 hover:text-white hover:bg-white/[0.06] rounded-full px-8 py-6 text-base font-outfit backdrop-blur-sm" data-testid="hero-features-btn">
                    See How It Works
                  </Button>
                </a>
              </motion.div>

              {/* Stats strip */}
              <motion.div
                className="grid grid-cols-2 sm:grid-cols-4 gap-8 pt-8 border-t border-white/[0.08]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: loaderDone ? 0.9 : 3.9, duration: 0.8 }}
              >
                {[
                  { val: '4.3M', label: 'Precision casts ready' },
                  { val: '62%', label: 'Token reduction avg' },
                  { val: '7', label: 'AI platforms supported' },
                  { val: '75', label: 'Professional domains' },
                ].map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (loaderDone ? 1 : 4) + i * 0.1, duration: 0.5 }}
                  >
                    <p className="font-outfit text-2xl sm:text-3xl font-bold text-white tracking-tight">{s.val}</p>
                    <p className="text-white/40 text-[11px] mt-1.5 font-light">{s.label}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              className="flex justify-center lg:justify-end"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: loaderDone ? 0.5 : 3.5, duration: 0.9, ease: EASE }}
            >
              <HeroLayerShowcase />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ TRY IT ═══ */}
      <section id="try-now" className="py-28 md:py-36 relative" data-testid="try-now-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#080808] to-[#030303]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
        <div className="relative z-[2] max-w-5xl mx-auto px-6 md:px-12">
          <motion.div className="mb-12" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VP} transition={{ duration: 0.7, ease: EASE }}>
            <span className="text-[#C084FC] text-[11px] font-semibold uppercase tracking-[3px] block mb-5">Try it now — no signup required</span>
            <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              See it work in <span className="pi-gradient-text">5 seconds.</span>
            </h2>
            <p className="text-white/50 max-w-md text-base mt-5 font-light">Type any message below or pick an example. No account needed.</p>
            <p className="text-white/30 text-sm mt-2">2 free tries · Then create a free account for 10/day</p>
          </motion.div>
          <TryNowDemo />
        </div>
      </section>

      {/* ═══ FEATURES — THE PLATFORM ═══ */}
      <section id="features" className="py-28 md:py-36 relative" data-testid="features-section">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />

        <div className="relative z-[2] max-w-5xl mx-auto px-6 md:px-12">
          <motion.div className="mb-20" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VP} transition={{ duration: 0.7, ease: EASE }}>
            <span className="text-[#C084FC] text-[11px] font-semibold uppercase tracking-[3px] block mb-5">The Platform</span>
            <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              Every tool you need.<br />
              <span className="pi-gradient-text">Zero knowledge required.</span>
            </h2>
            <p className="text-white/50 max-w-xl text-base mt-5 font-light">
              SendPI operates at the keyboard layer — detecting your context, scoring your intent, and delivering outcomes across every AI platform you use.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                className={`glass-card rounded-2xl p-7 group ${f.span}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5, ease: EASE }}
              >
                <span className="text-[10px] font-semibold uppercase tracking-widest block mb-3" style={{ color: f.tagColor }}>{f.tag}</span>
                <h3 className="font-outfit text-white font-bold text-xl mb-3 group-hover:pi-gradient-text transition-all">{f.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mb-5 font-light">{f.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {f.pills.map(p => (
                    <span key={p} className="px-3 py-1 text-[10px] font-medium rounded-full bg-white/[0.05] text-white/45 border border-white/[0.08]">{p}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="py-28 md:py-36 relative" data-testid="how-it-works-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#080808] to-[#030303]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#22D3EE]/[0.08] to-transparent" />

        <div className="relative z-[2] max-w-5xl mx-auto px-6 md:px-12">
          <motion.div className="mb-20" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VP} transition={{ duration: 0.7, ease: EASE }}>
            <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              Four steps.<br /><span className="text-white/40 font-light">Under 2 minutes.</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
            {HOW_IT_WORKS.map((step, i) => (
              <motion.div
                key={step.title}
                className="relative"
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.6, ease: EASE }}
              >
                <span className="font-outfit text-7xl font-bold text-white/[0.04] absolute -top-6 -left-2 select-none">0{step.num}</span>
                <div className="relative pt-8">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-6"
                    style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(34,211,238,0.15))', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <step.icon className="w-5 h-5 text-white/80" />
                  </div>
                  <h3 className="font-outfit text-white font-bold text-lg mb-3">{step.title}</h3>
                  <p className="text-white/45 text-sm leading-relaxed font-light">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ META-PROMPT PROBLEM ═══ */}
      <MetaPromptSection />

      {/* ═══ COMPARISON ═══ */}
      <ComparisonSection />

      <LiveTestimonials />

      {/* ═══ FINAL CTA ═══ */}
      <section className="relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-[0.06]"
            style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full opacity-[0.04]"
            style={{ background: 'radial-gradient(ellipse, #00E5FF 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-[2] py-32 md:py-40">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VP} transition={{ duration: 0.7, ease: EASE }}>
              <motion.img
                src="/sendpi-logo.png"
                alt="SendPI"
                className="h-20 w-auto mx-auto mb-12"
                style={{ filter: 'drop-shadow(0 0 40px rgba(168,85,247,0.4)) drop-shadow(0 0 80px rgba(34,211,238,0.2))' }}
                whileInView={{ scale: [0.9, 1], opacity: [0, 1] }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: EASE }}
              />
              <h2 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] tracking-tighter mb-8">
                State the outcome.<br className="hidden sm:block" />
                <span className="pi-gradient-text glow-text">Get it.</span>
              </h2>
            </motion.div>
          </div>
        </div>

        <div className="relative z-[2] py-16 bg-[#080808]/80 backdrop-blur-sm border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div className="grid md:grid-cols-2 gap-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={VP} transition={{ duration: 0.6, ease: EASE }}>
              <div className="border-l-2 border-[#A855F7]/40 pl-8 py-2">
                <span className="text-[#C084FC] text-xs font-semibold uppercase tracking-[0.2em]">People</span>
                <p className="font-outfit text-lg font-bold text-white mt-2 mb-2">Predict. Detect. Rewrite.</p>
                <p className="text-white/45 text-sm leading-relaxed font-light">12 outcome modes, 8 relationship contexts, 17 professions. PI Score 0–100.</p>
              </div>
              <div className="border-l-2 border-[#22D3EE]/40 pl-8 py-2">
                <span className="text-[#22D3EE] text-xs font-semibold uppercase tracking-[0.2em]">Machines</span>
                <p className="font-outfit text-lg font-bold text-white mt-2 mb-2">Cast. Optimize. Deliver.</p>
                <p className="text-white/45 text-sm leading-relaxed font-light">Precision casts for ChatGPT, Claude, Gemini, Perplexity, Copilot, Cursor & Emergent.</p>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="relative z-[2] py-28 md:py-36">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <motion.div className="space-y-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={VP} transition={{ duration: 0.6, ease: EASE }}>
              <p className="font-outfit text-2xl sm:text-3xl font-light text-white/50 leading-snug tracking-tight">
                Clarity. Confidence. <span className="text-white font-medium">Outcome.</span>
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a href="#try-now">
                  <Button className="shimmer-btn text-white rounded-full px-10 py-6 text-lg font-outfit font-semibold border-0" data-testid="final-cta-try-btn">
                    Try SendPI free <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <Link to="/signup">
                  <Button variant="outline" className="border-white/15 text-white/60 hover:text-white hover:bg-white/[0.06] rounded-full px-10 py-6 text-lg font-outfit" data-testid="final-cta-signup-btn">
                    Create account
                  </Button>
                </Link>
              </div>
              <p className="text-white/30 text-sm font-light">No credit card required · Works on any website</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
